import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DragDropModule, CdkDrag, CdkDragStart, CdkDragEnd } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import type { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import { combineLatest, debounceTime, exhaustMap, filter, finalize, map, Observable, of, startWith, Subject, take, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';

import { MailboxType, type IAccount } from '../../../../models/account.models';
import { AiDraftProcessingStatus, ComposeType, type IComposePanel, type IDraftPlaceholder, type IComposePanelAttachment } from '../../../../models/compose.models';
import { ComposeService } from '../../../../services/compose.service';
import { CommonService } from 'src/app/shared/common.service';
import { UiModule } from 'src/app/ui/ui.module';
import { AppState } from 'src/app/core/app.state';
import { selectAccounts } from '../../../../store/smart-inbox.selectors';
import * as smartInboxActions from '../../../../store/smart-inbox.actions';
import { buildReplyEditorBody } from '../../../../utils/email-body-sanitizer.util';
import { PlaceholdersConfirmDialogComponent } from '../placeholders-confirm-dialog/placeholders-confirm-dialog.component';
import { FileSizePipe } from 'src/app/config/filesize-pipe';
import { AiGenerationLoaderComponent } from './ai-generation-loader/ai-generation-loader.component';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_PANEL_WIDTH = 560;
const DEFAULT_PANEL_HEIGHT = 500;

/** Matches AI draft placeholders in body HTML: <[PLACEHOLDER_ID]> or the HTML-encoded &lt;[PLACEHOLDER_ID]&gt; that CKEditor produces. */
const PLACEHOLDER_TAG_REGEX = /(?:<|&lt;)\[([^\]]+)\](?:>|&gt;)/g;
/** Matches a bare placeholder token left as plain text in the editor: [PLACEHOLDER_ID] (uppercase snake-case). */
const PLACEHOLDER_TOKEN_REGEX = /\[([A-Z][A-Z0-9_]*)\]/g;

/** A clickable placeholder chip rendered in the overlay layer, positioned over its token in the editor. */
interface IPlaceholderChip {
  id: string;
  /**
   * 0-based rank of this token among same-id tokens, in document order. The same id can
   * appear many times (one per row in a quote table); this disambiguates which occurrence
   * a chip maps to so editing one cell does not overwrite the others.
   */
  occurrenceIndex: number;
  /** Position/size in pixels, relative to the overlay layer (the CKEditor wrapper). */
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Single draggable compose/reply/forward email panel rendered inside the
 * overlay host. Supports drag, resize, CKEditor body, email chip inputs
 * with autocomplete, and client-side validation before send.
 */
@Component({
  selector: 'app-compose-panel',
  standalone: true,
  templateUrl: './compose-panel.component.html',
  styleUrls: ['./compose-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatTooltipModule,
    CKEditorModule,
    UiModule,
    TranslateModule,
    FileSizePipe,
    AiGenerationLoaderComponent,
  ],
})
export class ComposePanelComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Static ─────────────────────────────────────────────────────────────────

  private static readonly CK_TOOLBAR_HEIGHT_PX = 39;
  private static readonly QUOTE_TOGGLE_HEIGHT_PX = 32;

  // ── Decorated properties ───────────────────────────────────────────────────

  @Input() panel!: IComposePanel;

  @Output() bringToFront = new EventEmitter<string>();
  @Output() minimize = new EventEmitter<string>();
  @Output() positionChanged = new EventEmitter<{ x: number; y: number }>();
  @Output() cancel = new EventEmitter<string>();
  @Output() send = new EventEmitter<string>();
  @Output() deleteDraft = new EventEmitter<string>();

  @ViewChild(CdkDrag) cdkDrag!: CdkDrag;
  @ViewChild('dialogRoot', { read: ElementRef }) dialogRoot!: ElementRef<HTMLElement>;
  @ViewChild('editorWrap', { read: ElementRef }) editorWrapRef!: ElementRef<HTMLElement>;
  @ViewChild('toEmailInput', { read: ElementRef }) toEmailInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('ccEmailInput', { read: ElementRef }) ccEmailInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('bccEmailInput', { read: ElementRef }) bccEmailInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('quoteToggleEl', { read: ElementRef }) quoteToggleRef?: ElementRef<HTMLElement>;
  @ViewChild('attachmentInput', { read: ElementRef }) attachmentInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('attachmentBtnWrap', { read: ElementRef }) attachmentBtnWrapRef?: ElementRef<HTMLElement>;
  @ViewChild('phInput', { read: ElementRef }) phInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('phLayer', { read: ElementRef }) phLayerRef?: ElementRef<HTMLElement>;

  // ── Public properties ──────────────────────────────────────────────────────

  accounts: IAccount[] = [];
  savingDraft = false;
  generatingAiDraft = false;
  reasoningExpanded = false;
  reasoningBannerDismissed = false;
  aiDraftGenerationFailed = false;
  showCc = false;
  showBcc = false;

  quoteExpanded = false;
  quoteHtml = '';
  /** Placeholders still present in the body; subset of panel.aiDraft.placeholders. */
  draftPlaceholders: IDraftPlaceholder[] = [];
  /** Clickable chips drawn over each placeholder token, positioned relative to the editor wrapper. */
  placeholderChips: IPlaceholderChip[] = [];
  /** ID of the placeholder chip currently being edited inline; null when no chip is active. */
  activePlaceholderId: string | null = null;
  /** Occurrence rank (document order, per id) of the chip being edited; -1 when none. */
  activeOccurrenceIndex = -1;
  activeInputTop = 0;
  activeInputLeft = 0;
  activeInputWidth = 160;
  activeInputHeight = 28;
  private editorInstance: any = null;
  private editableEl: HTMLElement | null = null;
  private chipRecomputeScheduled = false;
  private chipRecomputeRaf = 0;
  // Bound refs kept so ngOnDestroy can remove the listeners we add on the editable
  // (compose panels open/close frequently — don't rely on GC).
  private editableScrollHandler: (() => void) | null = null;
  private editableKeydownHandler: ((ev: KeyboardEvent) => void) | null = null;
  private suppressDraft = false;

  toControl = new FormControl('');
  ccControl = new FormControl('');
  bccControl = new FormControl('');

  filteredToSuggestions$!: Observable<string[]>;
  filteredCcSuggestions$!: Observable<string[]>;
  filteredBccSuggestions$!: Observable<string[]>;

  private allEmails: string[] = [];

  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  readonly Editor = ClassicEditor;
  editorConfig = {
    placeholder: 'Write your message...',
    toolbar: {
      shouldNotGroupWhenFull: true,
      items: ['undo', 'redo', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList', '|', 'link'],
    },
  };

  // ── Private properties ─────────────────────────────────────────────────────

  private _initialEditorData: string | undefined;
  private resizeObserver: ResizeObserver | null = null;
  private editorWrapResizeObserver: ResizeObserver | null = null;
  private destroy$ = new Subject<void>();
  private draftTrigger$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private actions$: Actions,
    private dialog: MatDialog,
    private composeService: ComposeService,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.panel.aiDraft) {
      const status = this.panel.aiDraft.processingStatus;
      this.aiDraftGenerationFailed = status === AiDraftProcessingStatus.REJECTED || status === AiDraftProcessingStatus.FAILED;
      this.reasoningBannerDismissed = !!this.panel.aiDraft.dismissed;
    }

    const isReply = this.panel.type === 'reply' || this.panel.type === 'reply-all';
    if (isReply && this.panel.reply_to_message) {
      this.quoteHtml = buildReplyEditorBody(this.panel.reply_to_message);
    }

    const allEmails$ = this.store.select((state) => state.settings.allUsers).pipe(
      takeUntil(this.destroy$),
      map((users) => users.map((user) => user.email)),
    );

    allEmails$.subscribe((emails) => { this.allEmails = emails; });

    this.filteredToSuggestions$ = combineLatest([
      this.toControl.valueChanges.pipe(startWith('')),
      allEmails$,
    ]).pipe(
      map(([typed, emails]) => this.filterEmails(typed || '', emails, this.toEmails)),
    );

    this.filteredCcSuggestions$ = combineLatest([
      this.ccControl.valueChanges.pipe(startWith('')),
      allEmails$,
    ]).pipe(
      map(([typed, emails]) => this.filterEmails(typed || '', emails, this.ccEmails)),
    );

    this.filteredBccSuggestions$ = combineLatest([
      this.bccControl.valueChanges.pipe(startWith('')),
      allEmails$,
    ]).pipe(
      map(([typed, emails]) => this.filterEmails(typed || '', emails, this.bccEmails)),
    );

    this.store.select(selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => { this.accounts = accounts; });

    this.draftTrigger$.pipe(
      debounceTime(500),
      exhaustMap(() => {
        this.savingDraft = true;
        this.cdr.markForCheck();
        this.composeService.saveDraft(this.panel.id);
        return this.actions$.pipe(
          ofType(
            smartInboxActions.CreateDraftSuccess,
            smartInboxActions.CreateDraftError,
            smartInboxActions.UpdateDraftSuccess,
            smartInboxActions.UpdateDraftError,
          ),
          filter((action) => action.panelId === this.panel.id),
          take(1),
          finalize(() => {
            this.savingDraft = false;
            this.cdr.markForCheck();
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe();

    this.actions$.pipe(
      ofType(smartInboxActions.GenerateAiDraftSuccess, smartInboxActions.GenerateAiDraftError),
      filter((action) => action.panelId === this.panel.id),
      takeUntil(this.destroy$),
    ).subscribe((action) => {
      if (action.type === smartInboxActions.GenerateAiDraftSuccess.type) {
        const { body, placeholders, reasoning, processingStatus, processingErrors } = action as ReturnType<typeof smartInboxActions.GenerateAiDraftSuccess>;
        const status = (processingStatus ?? undefined) as AiDraftProcessingStatus | undefined;
        this.aiDraftGenerationFailed = status === AiDraftProcessingStatus.REJECTED || status === AiDraftProcessingStatus.FAILED;

        this.processAiDraft(body, placeholders, reasoning, status, processingErrors);
        this.reasoningExpanded = false;
        if (this.editorInstance && !this.aiDraftGenerationFailed && body?.trim()) {
          this.suppressDraft = true;
          this.editorInstance.setData(this.panel.body);
          this.suppressDraft = false;
          this.focusEditorAtStart(this.editorInstance);
        }
      }
      this.generatingAiDraft = false;
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.showCc = this.panel.cc.length > 0;
    this.showBcc = this.panel.bcc.length > 0;

    const el = this.dialogRoot?.nativeElement;
    if (!el) return;

    // Track panel resize (CSS `resize: both`) and persist dimensions via the service.
    // We recompute placeholder chips here (not only in the editor-wrap observer): this
    // observer watches dialogRoot and does NOT mutate its observed box, so it fires every
    // frame during a drag-resize — whereas the wrap observer mutates the editable height
    // var, triggering a ResizeObserver loop that defers delivery until the drag stops
    // (which left the chips stuck at their old positions until release).
    this.resizeObserver = new ResizeObserver((entries) => {
      this.scheduleChipRecompute();
      const entry = entries[0];
      if (!entry) return;
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      if (this.panel.width === w && this.panel.height === h) return;
      this.composeService.updatePanelSize(this.panel.id, w, h);
    });
    this.resizeObserver.observe(el);

    // Keep the CKEditor editable area in sync with the editor wrapper height.
    // The CSS var --compose-editor-editable-max-height drives the editable's
    // max-height so it grows/shrinks as the panel is resized.
    const wrapEl = this.editorWrapRef?.nativeElement;
    if (wrapEl) {
      const syncEditorHeight = (wrapHeight: number): void => {
        const quoteOffset = this.canShowQuoteToggle && !this.quoteExpanded
          ? ComposePanelComponent.QUOTE_TOGGLE_HEIGHT_PX
          : 0;
        const editableMax = Math.max(140, wrapHeight - ComposePanelComponent.CK_TOOLBAR_HEIGHT_PX - quoteOffset);
        wrapEl.style.setProperty('--compose-editor-editable-max-height', `${editableMax}px`);
      };

      this.editorWrapResizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) syncEditorHeight(entry.contentRect.height);
        this.scheduleChipRecompute();
      });
      this.editorWrapResizeObserver.observe(wrapEl);

      // Set initial value immediately + after first layout frame (CKEditor may not have rendered its toolbar yet)
      syncEditorHeight(wrapEl.getBoundingClientRect().height);
      requestAnimationFrame(() => syncEditorHeight(wrapEl.getBoundingClientRect().height));
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.editorWrapResizeObserver?.disconnect();
    this.editorWrapResizeObserver = null;
    if (this.chipRecomputeRaf) cancelAnimationFrame(this.chipRecomputeRaf);
    if (this.editableEl) {
      if (this.editableScrollHandler) this.editableEl.removeEventListener('scroll', this.editableScrollHandler);
      if (this.editableKeydownHandler) this.editableEl.removeEventListener('keydown', this.editableKeydownHandler, true);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed (template getters) ────────────────────────────────────────────

  get isLoading(): boolean {
    return !!this.panel.sending;
  }

  get isDraft(): boolean {
    return !!this.panel.draftId;
  }

  get isSavingDraft(): boolean {
    return this.savingDraft;
  }

  get isNewCompose(): boolean {
    return this.panel.type === 'compose';
  }

  get isReplyType(): boolean {
    return this.panel.type === 'reply' || this.panel.type === 'reply-all';
  }

  get dialogWidth(): number {
    return this.panel.width ?? DEFAULT_PANEL_WIDTH;
  }

  get dialogHeight(): number {
    return this.panel.height ?? DEFAULT_PANEL_HEIGHT;
  }

  /** Negative half-width so panel stays centered (left: 50% in CSS). */
  get dialogMarginLeft(): number {
    return -this.dialogWidth / 2;
  }

  get senderAccounts(): IAccount[] {
    return this.accounts.filter(
      (account) =>
        account.mailbox_type === MailboxType.PERSONAL || account.mailbox_type === MailboxType.TEAM,
    );
  }

  get toEmails(): string[] {
    return this.parseEmails(this.panel.to);
  }

  get ccEmails(): string[] {
    return this.parseEmails(this.panel.cc);
  }

  get bccEmails(): string[] {
    return this.parseEmails(this.panel.bcc);
  }

  /** One-time snapshot of panel.body for CKEditor [data]; synced back via onBodyChange. */
  get initialEditorData(): string {
    if (this._initialEditorData === undefined) {
      const raw = this.panel.body || '';
      this._initialEditorData = raw;
      if (this.panel.aiDraft?.placeholders?.length) {
        const remainingIds = new Set(this.extractPlaceholdersFromBody(raw).map((p) => p.id));
        this.draftPlaceholders = this.panel.aiDraft.placeholders.filter((p) => remainingIds.has(p.id));
      } else {
        this.draftPlaceholders = this.extractPlaceholdersFromBody(raw);
      }
    }
    return this._initialEditorData;
  }

  get canShowQuoteToggle(): boolean {
    return !!this.quoteHtml;
  }

  get panelAttachments(): IComposePanelAttachment[] {
    return this.panel.attachments ?? [];
  }

  get hasUploadingAttachments(): boolean {
    return (this.panel.attachments ?? []).some((a) => !a.id && !!a.file);
  }

  isAttachmentUploading(att: IComposePanelAttachment): boolean {
    return !att.id && !!att.file;
  }

  // ── Attachments ───────────────────────────────────────────────────────────

  triggerAttachmentInput(): void {
    this.attachmentInputRef?.nativeElement?.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    const toAdd: IComposePanelAttachment[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      toAdd.push({ file, name: file.name, size: file.size });
    }
    this.composeService.addAttachments(this.panel.id, toAdd);
    input.value = '';
    this.cdr.markForCheck();
    this.draftTrigger$.next();
    if (this.panel.draftId != null) {
      this.store.dispatch(smartInboxActions.UploadDraftAttachments({ panelId: this.panel.id }));
    }
  }

  removeAttachment(att: IComposePanelAttachment): void {
    this.store.dispatch(smartInboxActions.RemoveDraftAttachment({ panelId: this.panel.id, attachment: att }));
    this.cdr.markForCheck();
    this.draftTrigger$.next();
  }

  trackByAttachment(_index: number, att: IComposePanelAttachment): number | File | string {
    return att.id ?? att.file ?? `${att.name}-${att.size}`;
  }

  // ── Email chip handlers ────────────────────────────────────────────────────

  addEmail(field: 'to' | 'cc' | 'bcc', event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    if (!value) return;

    const current = this.getEmailList(field);
    const alreadyExists = current.some((e) => e.trim().toLowerCase() === value.toLowerCase());

    if (this.isValidEmail(value) && !alreadyExists) {
      this.setEmails(field, [...current, value]);
      this.draftTrigger$.next();
    }

    event.chipInput?.clear();
    this.getControl(field).setValue('');
  }

  removeEmail(field: 'to' | 'cc' | 'bcc', email: string): void {
    const current = this.getEmailList(field);
    this.setEmails(field, current.filter((e) => e !== email));
    this.getControl(field).setValue(this.getControl(field).value);
    this.draftTrigger$.next();
  }

  onAutocompleteSelected(field: 'to' | 'cc' | 'bcc', email: string): void {
    if (!email?.trim()) return;
    const current = this.getEmailList(field);
    if (!current.some((e) => e.trim().toLowerCase() === email.trim().toLowerCase())) {
      this.setEmails(field, [...current, email.trim()]);
      this.draftTrigger$.next();
    }
    this.getControl(field).setValue('');
    const inputRef =
      field === 'to' ? this.toEmailInputRef : field === 'cc' ? this.ccEmailInputRef : this.bccEmailInputRef;
    if (inputRef?.nativeElement) inputRef.nativeElement.value = '';
  }

  // ── Drag & panel interaction ───────────────────────────────────────────────

  onPanelMouseDown(): void {
    this.bringToFront.emit(this.panel.id);
  }

  onDragStarted(_event: CdkDragStart): void {
    this.bringToFront.emit(this.panel.id);
  }

  onDragEnded(event: CdkDragEnd): void {
    const pos = event.source.getFreeDragPosition();
    this.positionChanged.emit({ x: pos.x, y: pos.y });
  }

  onClose(): void {
    if (this.toEmails.length > 0) {
      this.composeService.saveDraft(this.panel.id);
    }
    this.cancel.emit(this.panel.id);
  }

  onMinimize(): void {
    if (this.cdkDrag) {
      const pos = this.cdkDrag.getFreeDragPosition();
      this.positionChanged.emit({ x: pos.x, y: pos.y });
    }
    this.minimize.emit(this.panel.id);
  }

  onDeleteDraft(): void {
    this.deleteDraft.emit(this.panel.id);
  }

  // ── AI Draft ───────────────────────────────────────────────────────────────

  onAiDraft(): void {
    if (!this.editorInstance || this.generatingAiDraft) return;

    const conversationId = this.panel.reply_to_conversation?.id;
    if (!conversationId) return;

    this.generatingAiDraft = true;
    this.reasoningBannerDismissed = false;
    this.aiDraftGenerationFailed = false;
    this.composeService.updatePanelAiDraft(this.panel.id, undefined);
    this.cdr.markForCheck();
    this.store.dispatch(smartInboxActions.GenerateAiDraft({ panelId: this.panel.id, conversationId }));
  }

  toggleReasoning(): void {
    this.reasoningExpanded = !this.reasoningExpanded;
  }

  dismissReasoningBanner(): void {
    this.reasoningBannerDismissed = true;
    this.reasoningExpanded = false;
    if (this.panel.aiDraft) {
      this.composeService.updatePanelAiDraft(this.panel.id, { ...this.panel.aiDraft, dismissed: true });
    }
  }

  processAiDraft(body: string | null, placeholders?: IDraftPlaceholder[], reasoning?: string | null, processingStatus?: AiDraftProcessingStatus, processingErrors?: string | null): void {
    const hasBody = !!body?.trim();
    const resolvedPlaceholders = hasBody && placeholders?.length
      ? placeholders
      : hasBody ? this.extractPlaceholdersFromBody(body!) : [];
    const aiDraft = { placeholders: resolvedPlaceholders, reasoning: reasoning || '', processingStatus, processingErrors };
    this.composeService.updatePanelAiDraft(this.panel.id, aiDraft);

    if (!hasBody) return;

    this.draftPlaceholders = [...resolvedPlaceholders];
    this.activePlaceholderId = null;
    const existingBody = this.panel.body || '';
    // Normalize placeholder tokens to bare [ID] plain text. CKEditor preserves
    // plain text verbatim; the clickable chips are drawn as an overlay (see
    // recomputePlaceholderChips) rather than living inside the editor content.
    // Normalize the WHOLE combined body (draft + quoted reply): if a tag-form
    // token (<[ID]>) survived in the quoted reply, the chip DOM walk (bare [ID])
    // and replaceTokenOccurrence's string walk would count occurrences differently
    // and edit the wrong cell. Normalizing everything keeps a single token form.
    const raw = existingBody.trim() ? body! + '<p>&nbsp;</p>' + existingBody : body!;
    const combined = this.normalizeBodyTokens(raw);
    this._initialEditorData = combined;
    this.panel.body = combined;
    this.scheduleChipRecompute();
  }

  /**
   * Extracts placeholder ids from body HTML. Detects the tag format
   * (<[ID]> / &lt;[ID]&gt;) and the bare token format ([ID]) left as plain text.
   */
  extractPlaceholdersFromBody(body: string): IDraftPlaceholder[] {
    const ids = new Set<string>();
    let m: RegExpExecArray | null;
    const tagRe = new RegExp(PLACEHOLDER_TAG_REGEX.source, 'g');
    while ((m = tagRe.exec(body)) !== null) ids.add(m[1]);
    const tokenRe = new RegExp(PLACEHOLDER_TOKEN_REGEX.source, 'g');
    while ((m = tokenRe.exec(body)) !== null) ids.add(m[1]);
    return Array.from(ids, (id) => ({ id }));
  }

  // ── Inline placeholder editing (overlay chips) ──────────────────────────────

  /** Converts placeholder tag tokens (<[ID]> / &lt;[ID]&gt;) to bare [ID] plain text. */
  private normalizeBodyTokens(body: string): string {
    return body.replace(new RegExp(PLACEHOLDER_TAG_REGEX.source, 'g'), (_, id) => `[${id}]`);
  }

  trackByChip(_: number, chip: IPlaceholderChip): string {
    return chip.id + '#' + chip.occurrenceIndex;
  }

  /** Schedules a chip recompute on the next animation frame (coalesces bursts of changes). */
  private scheduleChipRecompute(): void {
    if (this.chipRecomputeScheduled) return;
    this.chipRecomputeScheduled = true;
    this.chipRecomputeRaf = requestAnimationFrame(() => {
      this.chipRecomputeScheduled = false;
      this.recomputePlaceholderChips();
    });
  }

  /**
   * Walks the editable DOM, finds each placeholder token [ID] and computes a
   * chip rectangle positioned relative to the overlay layer (the editor wrapper).
   * Coordinates are layer-relative so chips track the draggable panel without
   * recomputing on drag — only on content change, scroll and resize.
   */
  private recomputePlaceholderChips(): void {
    const layer = this.phLayerRef?.nativeElement;
    const editable = this.editableEl;
    if (!layer || !editable) {
      if (this.placeholderChips.length) { this.placeholderChips = []; this.cdr.markForCheck(); }
      return;
    }

    const knownIds = (this.panel.aiDraft?.placeholders ?? this.draftPlaceholders).map((p) => p.id);
    const layerRect = layer.getBoundingClientRect();
    const edRect = editable.getBoundingClientRect();
    // Visible editable band, in layer-relative coords (excludes the toolbar above).
    const visibleTop = edRect.top - layerRect.top;
    const visibleBottom = edRect.bottom - layerRect.top;
    const chips: IPlaceholderChip[] = [];

    // Per-id occurrence counter, incremented for EVERY same-id token in document order
    // (before any visibility/rect clip) so the index stays aligned with the Nth token in
    // panel.body — which is what submitActivePlaceholder replaces.
    const idOccurrence: Record<string, number> = {};
    const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue || '';
      if (text.indexOf('[') === -1) continue;
      const re = new RegExp(PLACEHOLDER_TOKEN_REGEX.source, 'g');
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        const id = m[1];
        // No known placeholder ids (e.g. a normal non-AI reply) ⇒ no chips at all,
        // so bare tags in quoted content ([EXTERNAL], [URGENT]) aren't made clickable.
        if (!knownIds.length || !knownIds.includes(id)) continue;
        const occurrenceIndex = (idOccurrence[id] = (idOccurrence[id] ?? -1) + 1);
        const range = document.createRange();
        range.setStart(node, m.index);
        range.setEnd(node, m.index + m[0].length);
        const rect = range.getClientRects()[0];
        if (!rect || rect.width === 0) continue;
        const top = rect.top - layerRect.top;
        // Clip chips scrolled outside the visible editable band (under toolbar / below fold).
        if (top + rect.height <= visibleTop || top >= visibleBottom) continue;
        chips.push({
          id,
          occurrenceIndex,
          top,
          left: rect.left - layerRect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    }

    this.placeholderChips = chips;
    this.cdr.markForCheck();
  }

  onPlaceholderChipClick(chip: IPlaceholderChip): void {
    this.activeInputTop = chip.top;
    this.activeInputLeft = chip.left;
    this.activeInputWidth = Math.max(160, chip.width);
    this.activeInputHeight = Math.max(28, chip.height);
    this.activePlaceholderId = chip.id;
    this.activeOccurrenceIndex = chip.occurrenceIndex;
    this.cdr.markForCheck();
    setTimeout(() => { this.phInputRef?.nativeElement?.focus(); });
  }

  submitActivePlaceholder(value: string): void {
    const id = this.activePlaceholderId;
    const targetIndex = this.activeOccurrenceIndex;
    this.activePlaceholderId = null;
    this.activeOccurrenceIndex = -1;
    if (!id) { this.cdr.markForCheck(); return; }
    const trimmed = value.trim();
    if (trimmed) {
      this.replaceTokenOccurrence(id, targetIndex, this.escapeHtml(trimmed));
    }
    this.scheduleChipRecompute();
    this.cdr.markForCheck();
  }

  /**
   * Replaces ONLY the Nth occurrence (document order) of a placeholder token with
   * `replacement` (use '' to delete). The same id can repeat — one per row in a quote
   * table — so a global replace would overwrite every identical cell; counting in order
   * and substituting just the target keeps each cell independent.
   */
  private replaceTokenOccurrence(id: string, targetIndex: number, replacement: string): void {
    const idEsc = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Matches the bare [ID] token and any residual tag form <[ID]> / &lt;[ID]&gt;,
    // in the same order the chips were enumerated (recomputePlaceholderChips).
    const occRe = new RegExp(`(?:&lt;|<)?\\[${idEsc}\\](?:&gt;|>)?`, 'g');
    let occ = -1;
    let replaced = false;
    this.panel.body = this.panel.body.replace(occRe, (match) => {
      occ += 1;
      if (occ === targetIndex) { replaced = true; return replacement; }
      return match;
    });
    if (!replaced) return;
    if (this.editorInstance) {
      this.suppressDraft = true;
      this.editorInstance.setData(this.panel.body);
      this.suppressDraft = false;
    }
    const remainingIds = new Set(this.extractPlaceholdersFromBody(this.panel.body).map((p) => p.id));
    this.draftPlaceholders = (this.panel.aiDraft?.placeholders ?? []).filter((p) => remainingIds.has(p.id));
    this.draftTrigger$.next();
  }

  /**
   * Backspace/Delete on a placeholder removes the WHOLE token at once instead of one
   * character at a time. Placeholders are plain [ID] text (the prebuilt CKEditor build
   * has no widget support), so without this each keypress would chip away a letter.
   * Installed as a capture-phase listener so it fires before CKEditor's own handler.
   */
  private installPlaceholderKeyHandler(editableEl: HTMLElement): void {
    this.editableKeydownHandler = (ev: KeyboardEvent) => {
      if (ev.key !== 'Backspace' && ev.key !== 'Delete') return;
      const sel = editableEl.ownerDocument.getSelection();
      if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
      const node = sel.focusNode;
      if (!node || node.nodeType !== Node.TEXT_NODE || !editableEl.contains(node)) return;
      const hit = this.findTokenOccurrenceAtCaret(editableEl, node as Text, sel.focusOffset, ev.key === 'Backspace' ? 'back' : 'fwd');
      if (!hit) return; // not on a placeholder → let CKEditor delete normally
      ev.preventDefault();
      ev.stopImmediatePropagation();
      this.replaceTokenOccurrence(hit.id, hit.occurrenceIndex, '');
      this.scheduleChipRecompute();
      this.cdr.markForCheck();
    };
    editableEl.addEventListener('keydown', this.editableKeydownHandler, true);
  }

  /**
   * If the caret (at `offset` in text node `target`) sits inside or against a [ID] token,
   * returns its id and occurrence rank (document order, per id) — aligned with
   * replaceTokenOccurrence's counting. `dir` is 'back' (Backspace: token ends at caret)
   * or 'fwd' (Delete: token starts at caret). Returns null when not on a token.
   */
  private findTokenOccurrenceAtCaret(
    editableEl: HTMLElement,
    target: Text,
    offset: number,
    dir: 'back' | 'fwd',
  ): { id: string; occurrenceIndex: number } | null {
    const idOccurrence: Record<string, number> = {};
    const walker = document.createTreeWalker(editableEl, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue || '';
      if (text.indexOf('[') === -1) continue;
      const re = new RegExp(PLACEHOLDER_TOKEN_REGEX.source, 'g');
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        const id = m[1];
        const occurrenceIndex = (idOccurrence[id] = (idOccurrence[id] ?? -1) + 1);
        if (node !== target) continue;
        const start = m.index;
        const end = m.index + m[0].length;
        const inside = offset > start && offset < end;
        const touches = dir === 'back' ? offset === end : offset === start;
        if (inside || touches) return { id, occurrenceIndex };
      }
    }
    return null;
  }

  cancelActivePlaceholder(): void {
    this.activePlaceholderId = null;
    this.cdr.markForCheck();
  }

  getPlaceholderHint(id: string): string {
    return id.toLowerCase().replace(/_/g, ' ');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── Editor & field toggles ─────────────────────────────────────────────────

  onEditorReady(editor: any): void {
    this.editorInstance = editor;
    if (this.panel.type !== 'compose') {
      this.focusEditorAtStart(editor);
    }
    this.injectQuoteToggle(editor);
    this.injectAttachmentButton(editor);

    // Track the editable element to position the placeholder chip overlay, and
    // recompute chip positions when the content scrolls inside the editable area.
    const editableEl: HTMLElement | undefined = editor.ui?.view?.editable?.element;
    if (editableEl) {
      this.editableEl = editableEl;
      this.editableScrollHandler = () => this.scheduleChipRecompute();
      editableEl.addEventListener('scroll', this.editableScrollHandler, { passive: true });
      this.installPlaceholderKeyHandler(editableEl);
    }
    this.scheduleChipRecompute();

    // One-click AI reply: when the panel was opened with autoAiDraft, kick off
    // generation as soon as the editor is ready (consume the flag so it runs once).
    if (this.panel.autoAiDraft) {
      this.panel.autoAiDraft = false;
      this.onAiDraft();
    }
  }

  onBodyChange(event: ChangeEvent): void {
    this.panel.body = event.editor.getData();
    if (!this.generatingAiDraft) {
      const remainingIds = new Set(this.extractPlaceholdersFromBody(this.panel.body).map((p) => p.id));
      this.draftPlaceholders = (this.panel.aiDraft?.placeholders ?? []).filter((p) => remainingIds.has(p.id));
    }
    if (!this.suppressDraft) {
      this.draftTrigger$.next();
    }
    this.scheduleChipRecompute();
    this.cdr.markForCheck();
  }

  expandQuote(): void {
    if (!this.editorInstance || !this.quoteHtml || this.quoteExpanded) return;

    this.suppressDraft = true;
    const current: string = this.editorInstance.getData();
    const withQuote = current + this.quoteHtml;
    this.editorInstance.setData(withQuote);
    this.panel.body = withQuote;
    this.panel.quoteAppended = true;
    this.quoteExpanded = true;
    this.suppressDraft = false;
    this.focusEditorAtStart(this.editorInstance);
    this.scheduleChipRecompute();
  }

  onSubjectInput(): void {
    this.draftTrigger$.next();
  }

  onSenderChange(): void {
    this.draftTrigger$.next();
  }

  toggleCc(): void {
    this.showCc = !this.showCc;
    if (!this.showCc) this.panel.cc = '';
  }

  toggleBcc(): void {
    this.showBcc = !this.showBcc;
    if (!this.showBcc) this.panel.bcc = '';
  }

  /** Compare sender objects by id so mat-select matches IAccountMailbox to IAccount. */
  compareSender = (a: { id?: number } | null, b: { id?: number } | null): boolean => {
    return a?.id != null && b?.id != null && a.id === b.id;
  };

  // ── Validation & send ──────────────────────────────────────────────────────

  onSend(): void {
    const errors: string[] = [];
    if (this.toEmails.length === 0) {
      errors.push('Add at least one recipient in To.');
    }
    if (!this.hasBodyContent()) {
      errors.push('Message body is required.');
    }
    if (this.draftPlaceholders.length > 0) {
      this.openPlaceholdersConfirmDialog();
      return;
    }
    const invalid = this.getInvalidEmails();
    for (const { field, email } of invalid) {
      errors.push(`Invalid email (${field}): ${email}`);
    }
    if (errors.length > 0) {
      this.commonService.showToast(errors, 'ERROR', {
        duration: Infinity, verticalPosition: 'bottom', horizontalPosition: 'right',
      });
      return;
    }
    this.send.emit(this.panel.id);
  }

  private openPlaceholdersConfirmDialog(): void {
    const dialogRef = this.dialog.open(PlaceholdersConfirmDialogComponent, {
      data: { placeholders: this.draftPlaceholders },
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe((sendAnyway) => {
      if (sendAnyway) {
        this.send.emit(this.panel.id);
      }
    });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /** Move the quote-toggle element inside the CKEditor DOM, between .ck-editor__main and .ck-editor__top. */
  private injectQuoteToggle(editor: any): void {
    const toggleEl = this.quoteToggleRef?.nativeElement;
    if (!toggleEl) return;

    const ckRoot: HTMLElement | undefined = editor.ui?.view?.element;
    const ckMain = ckRoot?.querySelector('.ck-editor__main');
    if (ckRoot && ckMain) {
      ckRoot.insertBefore(toggleEl, ckMain);
    }
  }

  /** Injects the attachment button into the CKEditor toolbar (.ck-toolbar__items) so it appears on the right. */
  private injectAttachmentButton(editor: any): void {
    const wrapEl = this.attachmentBtnWrapRef?.nativeElement;
    if (!wrapEl) return;

    const ckRoot: HTMLElement | undefined = editor.ui?.view?.element;
    const toolbarItems = ckRoot?.querySelector('.ck-toolbar__items');
    if (toolbarItems) {
      toolbarItems.appendChild(wrapEl);
    }
  }

  getComposeTypeIcon(type: ComposeType): string {
    switch (type) {
      case ComposeType.REPLY: return 'reply';
      case ComposeType.REPLY_ALL: return 'reply-all';
      case ComposeType.FORWARD: return 'forward';
      default: return 'inbox';
    }
  }

  private parseEmails(value: string): string[] {
    if (!value?.trim()) return [];
    return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }

  private setEmails(field: 'to' | 'cc' | 'bcc', emails: string[]): void {
    const value = emails.join(', ');
    if (field === 'to') this.panel.to = value;
    else if (field === 'cc') this.panel.cc = value;
    else this.panel.bcc = value;
  }

  private getControl(field: 'to' | 'cc' | 'bcc'): FormControl {
    return field === 'to' ? this.toControl : field === 'cc' ? this.ccControl : this.bccControl;
  }

  private getEmailList(field: 'to' | 'cc' | 'bcc'): string[] {
    return field === 'to' ? this.toEmails : field === 'cc' ? this.ccEmails : this.bccEmails;
  }

  private filterEmails(typed: string, allEmails: string[], selectedEmails: string[]): string[] {
    const selectedLower = new Set(selectedEmails.map((e) => e.trim().toLowerCase()));
    const available = allEmails.filter((e) => !selectedLower.has(e.trim().toLowerCase()));
    if (!typed) return available;
    const lower = typed.toLowerCase();
    return available.filter((e) => e.toLowerCase().includes(lower));
  }

  private isValidEmail(value: string): boolean {
    return EMAIL_REGEX.test(value.trim());
  }

  private getInvalidEmails(): { field: string; email: string }[] {
    const invalid: { field: string; email: string }[] = [];
    for (const email of this.toEmails) {
      if (!this.isValidEmail(email)) invalid.push({ field: 'To', email });
    }
    for (const email of this.ccEmails) {
      if (!this.isValidEmail(email)) invalid.push({ field: 'Cc', email });
    }
    for (const email of this.bccEmails) {
      if (!this.isValidEmail(email)) invalid.push({ field: 'Bcc', email });
    }
    return invalid;
  }

  private hasBodyContent(): boolean {
    const raw = this.panel.body ?? '';
    const text = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 0;
  }

  private focusEditorAtStart(editor: any): void {
    setTimeout(() => {
      editor.focus();
      const model = editor.model;
      const root = model.document.getRoot();
      if (root) {
        model.change((writer: any) => {
          writer.setSelection(root, 0);
        });
      }
    });
  }
}
